'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Heart } from 'lucide-react';
import type { FavoriteItem } from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { useCart } from '@paeffgen/react/crm-documents/feature-material-requests';
import {
  FavoriteCard,
  useFavorites,
  useFavoriteActionMenu,
} from '@paeffgen/react/crm-documents/feature-favorites';
import { Skeleton, EmptyState } from '@paeffgen/react/crm-documents/ui';
import { clsx } from 'clsx';

const PAGE_SIZE_OPTIONS = [10, 20, 30] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];
const PAGE_SIZE_STORAGE_KEY = 'dashboardFavoritesPageSize';
const DEFAULT_PAGE_SIZE: PageSize = 10;

// Maximal anpinnbare Favoriten — identisch zur Favoriten-Seite.
const MAX_PINNED = 6;

function readStoredPageSize(): PageSize {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  const raw = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
  const parsed = Number(raw);
  return PAGE_SIZE_OPTIONS.includes(parsed as PageSize)
    ? (parsed as PageSize)
    : DEFAULT_PAGE_SIZE;
}

function DashboardFavoritesSkeleton() {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-6">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="bg-white p-4 rounded-xl shadow-[0_2px_4px_rgba(44,44,43,0.05)] border border-zinc-200"
          >
            <div className="flex gap-4 mb-4">
              <Skeleton className="w-16 h-16 rounded-lg" />
              <div className="flex-1">
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-5 w-20 mb-4" />
            <div className="flex items-center gap-2 pt-3 border-t border-zinc-100">
              <Skeleton className="h-9 flex-1" />
              <Skeleton className="h-9 w-10" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardFavorites() {
  const { contextHeading } = useAdmin();

  // Geteilter Hook — dieselbe Datenquelle wie die Favoriten-Seite (inkl. Labels
  // + Farben), damit Karte und Kebab-Menü dort identisch funktionieren.
  const {
    favorites,
    colorByLabel,
    labels,
    loading,
    updateFavorite,
    toggleFavorite,
    toggleLabelOnArticle,
    addUserLabel,
  } = useFavorites();

  const { addItem, removeItem, items: cartItems } = useCart();
  // Indikator spiegelt den ECHTEN Warenkorb (nicht ein additives lokales Set),
  // damit „rauf und direkt wieder runter" korrekt grau bleibt.
  const isInCart = (articleNumber: string) =>
    cartItems.some((c) => c.articleNumber === articleNumber);

  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPageSize(readStoredPageSize());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  const allFavorites = favorites;

  // Backend sorts by created_at DESC (newest first). Stable sort keeps that
  // order intact within each pinned group, so the result is:
  // pinned (newest first) → unpinned (newest first).
  const displayedFavorites = useMemo(() => {
    const sorted = [...allFavorites].sort((a, b) => {
      if (a.pinned === b.pinned) return 0;
      return a.pinned ? -1 : 1;
    });
    return sorted.slice(0, pageSize);
  }, [allFavorites, pageSize]);

  // Toggle „zur Anfrage" — Menge fix 1 (kein Stepper mehr, exakt wie Favoriten-Seite).
  const handleAddToCart = useCallback(
    (article: FavoriteItem) => {
      if (isInCart(article.articleNumber)) {
        removeItem(article.articleNumber);
        return;
      }
      addItem(article.articleNumber, 1);
    },
    // isInCart liest cartItems direkt → als Dependency aufnehmen.
    [cartItems, addItem, removeItem],
  );

  const handleTogglePin = useCallback(
    (article: FavoriteItem, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!article.pinned) {
        const pinnedCount = favorites.filter((f) => f.pinned).length;
        if (pinnedCount >= MAX_PINNED) {
          return;
        }
      }
      updateFavorite(article.articleNumber, { pinned: !article.pinned });
    },
    [favorites, updateFavorite],
  );

  // Kebab-Menü (Sheet mobil / Popover Desktop) + Beleg-Dialog — geteilte
  // Orchestrierung mit der Favoriten-Seite.
  const { openActionMenu, overlays } = useFavoriteActionMenu({
    favorites,
    labels,
    updateFavorite,
    toggleFavorite,
    toggleLabelOnArticle,
    addUserLabel,
    onAddToCart: handleAddToCart,
  });

  if (loading && allFavorites.length === 0) {
    return <DashboardFavoritesSkeleton />;
  }

  if (allFavorites.length === 0) {
    return (
      <div className="mb-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-secondary tracking-tight">
            {contextHeading('Favoriten')}
          </h2>
          <Link
            href="/favoriten"
            className="text-sm font-bold text-primary hover:text-primary-hover flex items-center gap-1 transition-colors"
          >
            Favoriten verwalten <ArrowRight size={14} />
          </Link>
        </div>
        <div className="bg-white p-10 rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)]">
          <EmptyState
            icon={Heart}
            title="Noch keine Favoriten"
            description="Markieren Sie Artikel in der Artikelsuche oder auf Belegen als Favorit, um sie hier schnell wiederzufinden."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-6">
        <h2 className="text-xl font-bold text-secondary tracking-tight">
          {contextHeading('Favoriten')}
        </h2>
        <div className="flex items-center justify-between gap-3 sm:justify-end sm:gap-4">
          <div
            className="flex items-center bg-zinc-100 rounded-lg border border-zinc-200 p-1"
            role="group"
            aria-label="Anzahl Favoriten"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPageSize(n)}
                aria-pressed={pageSize === n}
                title={`${n} Favoriten anzeigen`}
                className={clsx(
                  'px-2.5 py-1 rounded-md text-xs font-bold tabular-nums transition-colors',
                  pageSize === n
                    ? 'bg-white shadow-sm text-primary'
                    : 'text-zinc-500 hover:text-zinc-700',
                )}
              >
                {n}
              </button>
            ))}
          </div>
          <Link
            href="/favoriten"
            className="text-sm font-bold text-primary hover:text-primary-hover flex items-center gap-1 transition-colors"
          >
            Favoriten verwalten <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {displayedFavorites.map((article) => (
          <FavoriteCard
            key={article.articleNumber}
            article={article}
            colorByLabel={colorByLabel}
            selectMode={false}
            isSelected={false}
            inCart={isInCart(article.articleNumber)}
            onToggleSelect={() => {
              /* kein Auswahlmodus auf dem Dashboard */
            }}
            onTogglePin={handleTogglePin}
            onAddToCart={handleAddToCart}
            onMenu={openActionMenu}
          />
        ))}
      </div>

      {allFavorites.length > displayedFavorites.length && (
        <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
          <span className="tabular-nums">
            Zeige {displayedFavorites.length} von {allFavorites.length} Favoriten
          </span>
          <Link
            href="/favoriten"
            className="font-bold text-primary hover:text-primary-hover flex items-center gap-1 transition-colors"
          >
            Alle anzeigen <ArrowRight size={12} />
          </Link>
        </div>
      )}

      {/* Kebab-Sheet/Popover + Beleg-Dialog. Bewusst als letztes Kind des
          (nicht-space-y) Wurzel-Containers — fixed-Overlays sonst mit Lücke. */}
      {overlays}
    </div>
  );
}
